import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { localizedServiceDetails } from "../../data/serviceDetails";
import PageShell, { PageHero } from "./PageShell";
import DoorsV2 from "../components/DoorsV2";
import { Arrow } from "../components/Primitives";
import { pickLang } from "../copy";
import { route } from "../paths";

const ART: Record<string, string> = {
  "nettoyage-professionnel": "/illustrations/landing/nettoyage.webp",
  "collecte-livraison": "/illustrations/landing/collecte.webp",
  "service-express": "/illustrations/landing/livraison.webp",
  "protection-premium": "/illustrations/landing/protection-scene.webp",
  "finition-luxe": "/illustrations/landing/finition.webp",
};

const Block: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="v2-reveal border-t border-ink/15 py-8 sm:py-10">
    <div className="mb-5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink/45">
      {label}
    </div>
    {children}
  </div>
);

const ServiceDetailV2: React.FC = () => {
  const { serviceId = "" } = useParams<{ serviceId: string }>();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = pickLang(currentLanguage);
  const isFr = lang === "fr";
  const service = localizedServiceDetails[lang][serviceId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  const back = () =>
    window.history.length > 1
      ? navigate(-1)
      : navigate(route(lang, "services"));

  const crumbs = [
    { label: isFr ? "Accueil" : "Home", to: route(lang) },
    { label: "Services", to: route(lang, "services") },
    { label: service?.title ?? "—" },
  ];

  if (!service) {
    return (
      <PageShell
        seo={{
          title: `${t("serviceDetail.notFound")} | Lave Tes Baskets`,
          description: t("serviceDetail.notFound"),
          canonicalPath: route(lang, "services"),
        }}
      >
        <PageHero
          crumbs={crumbs}
          index="—"
          eyebrow="404"
          title={t("serviceDetail.notFound")}
          actions={
            <Link to={route(lang, "services")} className="v2-btn v2-btn-solid">
              {t("serviceDetail.backToServices")}
              <Arrow />
            </Link>
          }
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      seo={{
        title: isFr
          ? `${service.title} : ${service.isOption ? "option" : "service"} de nettoyage de baskets | Lave Tes Baskets`
          : `${service.title}: sneaker cleaning ${service.isOption ? "add-on" : "service"} | Lave Tes Baskets`,
        description: service.shortDescription,
        canonicalPath: route(lang, `service/${serviceId}`),
        jsonLd: [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.title,
            description: service.shortDescription,
            provider: { "@type": "LocalBusiness", name: "Lave Tes Baskets" },
            areaServed: "Île-de-France",
          },
        ],
      }}
    >
      <PageHero
        crumbs={crumbs}
        index={service.icon}
        eyebrow={
          service.isOption
            ? isFr
              ? "Option · s'ajoute à un service de base"
              : "Add-on · attaches to a base service"
            : "Service"
        }
        title={service.title}
        lede={service.shortDescription}
        actions={
          <>
            <Link to={`/${lang}/signup`} className="v2-btn v2-btn-solid">
              {t("serviceDetail.bookService")}
              <Arrow />
            </Link>
            <button onClick={back} className="v2-btn v2-btn-ghost">
              {t("serviceDetail.backToServices")}
            </button>
          </>
        }
        aside={
          ART[serviceId] ? (
            <img
              src={ART[serviceId]}
              alt=""
              className="ml-auto max-h-[420px] w-auto max-w-full object-contain"
            />
          ) : undefined
        }
      />

      <section className="bg-bone py-16 text-ink sm:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-4 sm:px-6 lg:grid-cols-12">
          {/* summary */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <div className="v2-reveal rounded-2xl border border-ink/15 p-6 sm:p-7">
              {service.isOption && (
                <div className="mb-5 rounded-xl bg-ink p-4 text-bone">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-tangerine">
                    {isFr ? "Option" : "Add-on"}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-bone/80">
                    {isFr
                      ? "Cette option s'active lors de la commande, une fois votre service de base choisi : Standard, Luxe ou Enfant."
                      : "This add-on is enabled when ordering, once you have chosen your base service: Standard, Luxury or Kids."}
                  </p>
                </div>
              )}
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/45">
                {t("serviceDetail.summary")}
              </div>
              <dl className="mt-5 space-y-5">
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-deep">
                    {t("serviceDetail.duration")}
                  </dt>
                  <dd className="mt-1 font-display text-2xl leading-tight">
                    {service.duration}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-deep">
                    {t("serviceDetail.price")}
                  </dt>
                  <dd className="mt-1 font-display text-2xl leading-tight">
                    {service.price}
                  </dd>
                </div>
                {service.guarantee && (
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-deep">
                      {t("serviceDetail.guarantee")}
                    </dt>
                    <dd className="mt-1 text-sm text-ink/70">
                      {service.guarantee}
                    </dd>
                  </div>
                )}
              </dl>
              <Link
                to={`/${lang}/signup`}
                className="v2-btn v2-btn-ink mt-7 w-full justify-center"
              >
                {t("serviceDetail.bookService")}
                <Arrow />
              </Link>
            </div>
          </aside>

          {/* content */}
          <div className="lg:col-span-8">
            <p className="v2-reveal font-display text-[clamp(1.5rem,2.6vw,2.25rem)] leading-[1.25] tracking-[-0.01em]">
              {service.fullDescription}
            </p>

            {service.process?.length > 0 && (
              <Block label={t("serviceDetail.ourProcess")}>
                <ol className="space-y-0">
                  {service.process.map((step, i) => (
                    <li
                      key={i}
                      className="grid grid-cols-[3rem_1fr] gap-4 border-t border-ink/10 py-4 first:border-0 sm:grid-cols-[4rem_1fr]"
                    >
                      <span className="pt-1 font-mono text-[11px] tracking-[0.22em] text-teal-deep">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-ink/80">{step}</span>
                    </li>
                  ))}
                </ol>
              </Block>
            )}

            {service.materials?.length > 0 && (
              <Block label={t("serviceDetail.materialsHandled")}>
                <div className="flex flex-wrap gap-2">
                  {service.materials.map((m, i) => (
                    <span
                      key={i}
                      className="rounded-full border border-ink/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em]"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </Block>
            )}

            {(service.precautions?.length > 0 ||
              service.limits?.length > 0) && (
              <div className="v2-reveal mt-2 grid gap-4 border-t border-ink/15 pt-8 sm:grid-cols-2">
                {service.precautions?.length > 0 && (
                  <div className="rounded-2xl bg-ink p-6 text-bone">
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-tangerine">
                      {t("serviceDetail.importantPrecautions")}
                    </div>
                    <ul className="mt-4 space-y-2.5 text-sm text-bone/80">
                      {service.precautions.map((p, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="mt-[9px] h-px w-3 flex-shrink-0 bg-tangerine" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {service.limits?.length > 0 && (
                  <div className="rounded-2xl border border-ink/15 p-6">
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/45">
                      {t("serviceDetail.serviceLimits")}
                    </div>
                    <ul className="mt-4 space-y-2.5 text-sm text-ink/70">
                      {service.limits.map((l, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="mt-[9px] h-px w-3 flex-shrink-0 bg-ink/40" />
                          {l}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-ink py-16 text-bone sm:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <DoorsV2 />
        </div>
      </section>
    </PageShell>
  );
};

export default ServiceDetailV2;
